import {
  TCourseStructure,
  TCourseStructureNode,
  TPlaylistContentItem,
} from '../types/responses/video';

export type TCourseIndexedItem = {
  item: TPlaylistContentItem;
  index: number;
};

export type TTopicGroup = {
  key: string;
  number: string | null;
  title: string;
  items: TCourseIndexedItem[];
  completedCount: number;
  isFullyCompleted: boolean;
  containsCurrent: boolean;
};

export type TCourseTreeNodeType =
  | 'module'
  | 'topic'
  | 'lesson'
  | 'subtopic'
  | 'material'
  | 'placeholder';

export type TCourseTreeNode = {
  key: string;
  title: string;
  type: TCourseTreeNodeType;
  children: TCourseTreeNode[];
  description?: string | null;
  benefit?: string[] | string | null;
  itemRef?: TCourseIndexedItem;
  completedCount: number;
  totalCount: number;
  isFullyCompleted: boolean;
  containsCurrent: boolean;
  locked: boolean;
};

export type TCourseHierarchyPlacement = {
  moduleId?: string;
  moduleTitle: string;
  topicId?: string;
  topicTitle: string;
  lessonId?: string;
  lessonTitle?: string;
  subtopicId?: string;
  subtopicTitle?: string;
};

export type TCourseHierarchyAppendResult = {
  courseStructure: TCourseStructure;
  items: TPlaylistContentItem[];
  addedItemCount: number;
  linkedItemCount: number;
};

const TOPIC_SLUG_PATTERN = /(?:^|\/)topic-(\d+(?:\.\d+)*)-([a-z0-9][a-z0-9-]*)/;
const TOPIC_SEGMENT_PATTERN = /topic[\s._-]*(\d+(?:\.\d+)*)(?:[.\s:_-]+(.+))?/i;
const MODULE_SEGMENT_PATTERN = /(\d+)\s*module/i;
const MODULE_TITLE_PATTERN = /module\s*(\d+)/i;

const humanizeSlug = (slug: string): string => {
  const text = slug.replace(/-/g, ' ').trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const normalizeId = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const createStructureNodeId = (
  type: TCourseStructureNode['type'],
  title: string,
  siblings: TCourseStructureNode[]
): string => {
  const titleId = normalizeId(title) || 'untitled';
  const baseId = `${type}-${titleId}`;
  let id = baseId;
  let index = 2;

  while (siblings.some((node) => node.id === id)) {
    id = `${baseId}-${index}`;
    index += 1;
  }

  return id;
};

const cloneStructureNode = (
  node: TCourseStructureNode
): TCourseStructureNode => ({
  ...node,
  children: node.children?.map(cloneStructureNode),
  itemIndexes: node.itemIndexes ? [...node.itemIndexes] : undefined,
});

const cloneCourseStructure = (
  courseStructure?: TCourseStructure | null
): TCourseStructure => ({
  nodes: courseStructure?.nodes?.map(cloneStructureNode) ?? [],
  sourcePlaylistIds: courseStructure?.sourcePlaylistIds
    ? [...courseStructure.sourcePlaylistIds]
    : undefined,
  version: 1,
});

const sanitizeCourseItem = (
  item: TPlaylistContentItem
): TPlaylistContentItem => {
  const { completed, locked, order, ...rest } = item;
  return rest;
};

const findOrCreateStructureNode = (
  siblings: TCourseStructureNode[],
  type: TCourseStructureNode['type'],
  title: string,
  id?: string
): TCourseStructureNode => {
  const existing = id
    ? siblings.find((node) => node.id === id && node.type === type)
    : siblings.find(
        (node) =>
          node.type === type && normalizeId(node.title) === normalizeId(title)
      );

  if (existing) return existing;

  const nextNode: TCourseStructureNode = {
    children: [],
    id: createStructureNodeId(type, title, siblings),
    title,
    type,
  };

  siblings.push(nextNode);
  return nextNode;
};

const appendUniqueIndexes = (
  node: TCourseStructureNode,
  itemIndexes: number[]
) => {
  const currentIndexes = new Set(node.itemIndexes ?? []);
  itemIndexes.forEach((itemIndex) => currentIndexes.add(itemIndex));
  node.itemIndexes = Array.from(currentIndexes).sort(
    (left, right) => left - right
  );
};

export function appendPlaylistToCourseHierarchy({
  courseStructure,
  courseItems,
  placement,
  sourcePlaylistId,
  sourceItems,
}: {
  courseStructure?: TCourseStructure | null;
  courseItems: TPlaylistContentItem[];
  placement: TCourseHierarchyPlacement;
  sourcePlaylistId?: string;
  sourceItems: TPlaylistContentItem[];
}): TCourseHierarchyAppendResult {
  const nextStructure = cloneCourseStructure(courseStructure);
  const nextItems = [...courseItems];
  const existingIndexesByLinkId = new Map<string, number>();

  nextItems.forEach((item, index) => {
    existingIndexesByLinkId.set(item.linkId, index);
  });

  const linkedItemIndexes = sourceItems.map((item) => {
    const existingIndex = existingIndexesByLinkId.get(item.linkId);
    if (typeof existingIndex === 'number') {
      return existingIndex;
    }

    const nextIndex = nextItems.length;
    nextItems.push(sanitizeCourseItem(item));
    existingIndexesByLinkId.set(item.linkId, nextIndex);
    return nextIndex;
  });

  const addedItemCount = nextItems.length - courseItems.length;
  const moduleNode = findOrCreateStructureNode(
    nextStructure.nodes,
    'module',
    placement.moduleTitle,
    placement.moduleId
  );
  moduleNode.children ??= [];
  const topicNode = findOrCreateStructureNode(
    moduleNode.children,
    'topic',
    placement.topicTitle,
    placement.topicId
  );
  const targetNode =
    placement.lessonTitle?.trim() || placement.lessonId
      ? findOrCreateStructureNode(
          (topicNode.children ??= []),
          'lesson',
          placement.lessonTitle?.trim() || 'Lesson',
          placement.lessonId
        )
      : topicNode;

  if (placement.subtopicTitle?.trim() || placement.subtopicId) {
    const subtopicNode = findOrCreateStructureNode(
      (targetNode.children ??= []),
      'subtopic',
      placement.subtopicTitle?.trim() || 'Subtopic',
      placement.subtopicId
    );
    appendUniqueIndexes(subtopicNode, linkedItemIndexes);
  } else {
    appendUniqueIndexes(targetNode, linkedItemIndexes);
  }

  if (sourcePlaylistId) {
    nextStructure.sourcePlaylistIds = Array.from(
      new Set([...(nextStructure.sourcePlaylistIds ?? []), sourcePlaylistId])
    );
  }

  return {
    addedItemCount,
    courseStructure: nextStructure,
    items: nextItems,
    linkedItemCount: linkedItemIndexes.length,
  };
}

const cleanSegmentTitle = (value: string): string =>
  value.replace(/_/g, ' ').replace(/\s+/g, ' ').trim();

const cleanTopicTitle = (title: string, topicNumber: string): string => {
  const normalized = cleanSegmentTitle(title);
  const escapedNumber = topicNumber.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  return normalized
    .replace(new RegExp(`^topic\\s+${escapedNumber}[\\s.:_-]*`, 'i'), '')
    .replace(new RegExp(`^${escapedNumber}[\\s.:_-]+`, 'i'), '')
    .trim();
};

const getTopicNumberFromNode = (node: TCourseStructureNode): string | null => {
  const titleMatch = node.title.match(/topic\s+(\d+(?:\.\d+)*)/i);
  if (titleMatch) return titleMatch[1];

  const idMatch = node.id.match(/topic-(\d+(?:\.\d+)*)/i);
  return idMatch?.[1] ?? null;
};

const getStructuredNodeTitle = (node: TCourseStructureNode): string => {
  if (node.type !== 'topic') return node.title;

  const topicNumber = getTopicNumberFromNode(node);
  if (!topicNumber) return node.title;

  const topicTitle = cleanTopicTitle(node.title, topicNumber);
  return `Topic ${topicNumber}${topicTitle ? `. ${topicTitle}` : ''}`;
};

export function getCurrentItemIndex(
  items: TPlaylistContentItem[]
): number | null {
  const index = items.findIndex((item) => !item.completed && !item.locked);
  return index === -1 ? null : index;
}

type TTopicRef = {
  number: string;
  title: string;
};

type TModuleRef = {
  key: string;
  title: string;
};

const resolveTopic = (item: TPlaylistContentItem): TTopicRef | null => {
  if (item.topicNumber) {
    return {
      number: item.topicNumber,
      title: item.topicTitle
        ? cleanTopicTitle(item.topicTitle, item.topicNumber)
        : '',
    };
  }

  const match = item.s3Key?.match(TOPIC_SLUG_PATTERN);
  if (match) {
    return { number: match[1], title: humanizeSlug(match[2]) };
  }

  const topicSegment = item.s3Key
    ?.split('/')
    .find((segment) => TOPIC_SEGMENT_PATTERN.test(segment));
  const segmentMatch = topicSegment?.match(TOPIC_SEGMENT_PATTERN);
  if (segmentMatch) {
    return {
      number: segmentMatch[1],
      title: cleanSegmentTitle(segmentMatch[2] || topicSegment || ''),
    };
  }

  return null;
};

const resolveModule = (item: TPlaylistContentItem): TModuleRef => {
  if (item.moduleName) {
    const moduleTitleMatch = item.moduleName.match(MODULE_TITLE_PATTERN);

    if (moduleTitleMatch) {
      return {
        key: `module-${moduleTitleMatch[1]}`,
        title: item.moduleName,
      };
    }

    return {
      key: item.moduleId || normalizeId(item.moduleName),
      title: item.moduleName,
    };
  }

  const moduleSegment = item.s3Key?.split('/')[1] ?? '';
  const match = moduleSegment.match(MODULE_SEGMENT_PATTERN);
  if (match) {
    return {
      key: `module-${match[1]}`,
      title: `Module ${match[1]}`,
    };
  }

  return { key: 'module-1', title: 'Module 1' };
};

export function getTopicGroups(items: TPlaylistContentItem[]): TTopicGroup[] {
  const currentIndex = getCurrentItemIndex(items);
  const groups: TTopicGroup[] = [];
  const byNumber = new Map<string, TTopicGroup>();
  let fallbackGroup: TTopicGroup | undefined;
  let anyTopicResolved = false;

  items.forEach((item, index) => {
    const topic = resolveTopic(item);
    let group: TTopicGroup;

    if (topic) {
      anyTopicResolved = true;
      const existing = byNumber.get(topic.number);
      if (existing) {
        group = existing;
      } else {
        group = {
          completedCount: 0,
          containsCurrent: false,
          isFullyCompleted: false,
          items: [],
          key: topic.number,
          number: topic.number,
          title: topic.title,
        };
        byNumber.set(topic.number, group);
        groups.push(group);
      }
    } else {
      if (!fallbackGroup) {
        fallbackGroup = {
          completedCount: 0,
          containsCurrent: false,
          isFullyCompleted: false,
          items: [],
          key: 'other',
          number: null,
          title: 'Other materials',
        };
      }
      group = fallbackGroup;
    }

    group.items.push({ item, index });
    if (item.completed) {
      group.completedCount += 1;
    }
    if (index === currentIndex) {
      group.containsCurrent = true;
    }
  });

  if (fallbackGroup) {
    if (!anyTopicResolved) {
      fallbackGroup.key = 'all';
      fallbackGroup.title = 'All materials';
    }
    groups.push(fallbackGroup);
  }

  groups.forEach((group) => {
    group.isFullyCompleted =
      group.items.length > 0 && group.completedCount === group.items.length;
  });

  return groups;
}

const createMaterialNode = (
  itemRef: TCourseIndexedItem,
  currentIndex: number | null
): TCourseTreeNode => ({
  benefit: itemRef.item.benefit,
  children: [],
  completedCount: itemRef.item.completed ? 1 : 0,
  description: itemRef.item.description,
  containsCurrent: itemRef.index === currentIndex,
  isFullyCompleted: Boolean(itemRef.item.completed),
  itemRef,
  key: `material-${itemRef.index}-${itemRef.item.linkId}`,
  locked: Boolean(itemRef.item.locked),
  title: itemRef.item.name,
  totalCount: 1,
  type: 'material',
});

const rollupNode = (node: TCourseTreeNode): TCourseTreeNode => {
  if (node.type === 'material') return node;

  const children = node.children.map(rollupNode);
  const totalCount = children.reduce((sum, child) => sum + child.totalCount, 0);
  const completedCount = children.reduce(
    (sum, child) => sum + child.completedCount,
    0
  );

  return {
    ...node,
    children,
    completedCount,
    containsCurrent: children.some((child) => child.containsCurrent),
    isFullyCompleted: totalCount > 0 && completedCount === totalCount,
    locked: children.length > 0 && children.every((child) => child.locked),
    totalCount,
  };
};

const createGroupNode = (
  key: string,
  title: string,
  type: Exclude<TCourseTreeNodeType, 'material' | 'placeholder'>,
  children: TCourseTreeNode[],
  metadata?: Pick<TCourseTreeNode, 'description' | 'benefit'>
): TCourseTreeNode =>
  rollupNode({
    benefit: metadata?.benefit,
    children,
    completedCount: 0,
    description: metadata?.description,
    containsCurrent: false,
    isFullyCompleted: false,
    key,
    locked: false,
    title,
    totalCount: 0,
    type,
  });

const mapStructuredNode = (
  node: TCourseStructureNode,
  items: TPlaylistContentItem[],
  currentIndex: number | null
): TCourseTreeNode => {
  const childNodes =
    node.children
      ?.map((child) => mapStructuredNode(child, items, currentIndex))
      .filter(Boolean) ?? [];
  const materialNodes =
    node.itemIndexes
      ?.map((itemIndex) => {
        const item = items[itemIndex];
        if (!item) return null;
        return createMaterialNode({ item, index: itemIndex }, currentIndex);
      })
      .filter((child): child is TCourseTreeNode => Boolean(child)) ?? [];
  const children = [...childNodes, ...materialNodes];

  return createGroupNode(
    node.id,
    getStructuredNodeTitle(node),
    node.type,
    children,
    {
      benefit: node.benefit,
      description: node.description,
    }
  );
};

const getTopicKey = (topic: TTopicRef | null): string =>
  topic ? `topic-${topic.number}` : 'topic-other';

const getTopicTitle = (topic: TTopicRef | null): string =>
  topic
    ? `Topic ${topic.number}${
        topic.title ? `. ${cleanTopicTitle(topic.title, topic.number)}` : ''
      }`
    : 'Other materials';

type TLessonRef = {
  key: string;
  title: string;
};

const getTopicPathIndex = (s3Key: string | undefined): number => {
  const parts = s3Key?.split('/') ?? [];

  return parts.findIndex((segment) => TOPIC_SEGMENT_PATTERN.test(segment));
};

const compactRepeatedPrefix = (words: string[]): string[] => {
  for (let size = Math.floor(words.length / 2); size >= 1; size -= 1) {
    const first = words.slice(0, size).join(' ');
    const second = words.slice(size, size * 2).join(' ');

    if (first === second) {
      return words.slice(size);
    }
  }

  return words;
};

const cleanLessonTitle = (segment: string): string => {
  const normalized = segment
    .replace(/^\d+[\s._-]+/, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b(\d+)\s+(\d+)\b/g, '$1.$2')
    .replace(/\s+/g, ' ')
    .trim();
  const words = compactRepeatedPrefix(normalized.toLowerCase().split(' '));
  const title = words.join(' ');

  return title.charAt(0).toUpperCase() + title.slice(1);
};

const createLessonRef = (
  topic: TTopicRef | null,
  title: string
): TLessonRef => ({
  key: `${getTopicKey(topic)}-lesson-${normalizeId(title)}`,
  title,
});

const resolveLessonFromItemName = (
  item: TPlaylistContentItem,
  topic: TTopicRef | null
): TLessonRef | null => {
  const topicSuffix = topic?.number ? ` ${topic.number}` : '';
  const haystack = `${item.name} ${item.s3Key ?? ''}`.toLowerCase();

  if (
    /\bindependent\b/.test(haystack) ||
    haystack.includes('longread') ||
    haystack.includes('comparison_table')
  ) {
    return createLessonRef(topic, `Independent work${topicSuffix}`);
  }

  if (
    haystack.includes('screencast') ||
    haystack.includes('lifecycle') ||
    haystack.includes('decision_checklist') ||
    haystack.includes('handout') ||
    haystack.includes('screens')
  ) {
    return createLessonRef(topic, `Screencast${topicSuffix}`);
  }

  if (
    haystack.includes('seminar') ||
    haystack.includes('activ') ||
    haystack.includes('case') ||
    haystack.includes('worksheet') ||
    haystack.includes('reflection') ||
    haystack.includes('reference') ||
    haystack.includes('summary_table')
  ) {
    return createLessonRef(topic, `Seminar${topicSuffix}`);
  }

  if (
    haystack.includes('workshop') ||
    haystack.includes('assignment') ||
    haystack.includes('control form')
  ) {
    return createLessonRef(topic, `Assignment${topicSuffix}`);
  }

  if (
    haystack.includes('video lecture') ||
    haystack.includes('lecture') ||
    (topic?.number && haystack.includes(`${topic.number}.mp4`))
  ) {
    return createLessonRef(topic, `Video lecture${topicSuffix}`);
  }

  return null;
};

const resolveLesson = (item: TPlaylistContentItem): TLessonRef | null => {
  const parts = item.s3Key?.split('/') ?? [];
  const topicIndex = getTopicPathIndex(item.s3Key);
  const topic = resolveTopic(item);

  if (topicIndex === -1) {
    return resolveLessonFromItemName(item, topic);
  }

  const lessonSegment = parts
    .slice(topicIndex + 1, -1)
    .find(
      (segment) =>
        !['files', 'file', 'videos', 'video'].includes(segment.toLowerCase())
    );

  if (!lessonSegment) {
    return (
      resolveLessonFromItemName(item, topic) ??
      createLessonRef(topic, 'Topic materials')
    );
  }

  return {
    key: `${getTopicKey(topic)}-lesson-${normalizeId(lessonSegment)}`,
    title: cleanLessonTitle(lessonSegment),
  };
};

const buildAutomaticCourseTree = (
  items: TPlaylistContentItem[],
  fallbackModuleCount = 6
): TCourseTreeNode[] => {
  const currentIndex = getCurrentItemIndex(items);
  const moduleMap = new Map<
    string,
    {
      ref: TModuleRef;
      topicMap: Map<
        string,
        {
          title: string;
          lessonMap: Map<
            string,
            { title: string; children: TCourseTreeNode[] }
          >;
        }
      >;
    }
  >();

  Array.from({ length: Math.max(fallbackModuleCount, 1) }, (_, index) => {
    const moduleNumber = index + 1;
    moduleMap.set(`module-${moduleNumber}`, {
      ref: {
        key: `module-${moduleNumber}`,
        title: `Module ${moduleNumber}`,
      },
      topicMap: new Map(),
    });
  });

  items.forEach((item, index) => {
    const moduleRef = resolveModule(item);
    const moduleEntry = moduleMap.get(moduleRef.key) ?? {
      ref: moduleRef,
      topicMap: new Map<
        string,
        {
          title: string;
          lessonMap: Map<
            string,
            { title: string; children: TCourseTreeNode[] }
          >;
        }
      >(),
    };
    const topic = resolveTopic(item);
    const topicKey = getTopicKey(topic);
    const topicEntry = moduleEntry.topicMap.get(topicKey) ?? {
      lessonMap: new Map<
        string,
        { title: string; children: TCourseTreeNode[] }
      >(),
      title: getTopicTitle(topic),
    };
    const lesson = resolveLesson(item);
    const lessonKey = lesson?.key ?? `${topicKey}-lesson-materials`;
    const lessonEntry = topicEntry.lessonMap.get(lessonKey) ?? {
      children: [],
      title: lesson?.title ?? 'Topic materials',
    };

    lessonEntry.children.push(
      createMaterialNode({ item, index }, currentIndex)
    );
    topicEntry.lessonMap.set(lessonKey, lessonEntry);
    moduleEntry.topicMap.set(topicKey, topicEntry);
    moduleMap.set(moduleRef.key, moduleEntry);
  });

  return Array.from(moduleMap.values()).map((moduleEntry) =>
    createGroupNode(
      moduleEntry.ref.key,
      moduleEntry.ref.title,
      'module',
      Array.from(moduleEntry.topicMap.entries()).map(([key, topic]) =>
        createGroupNode(
          key,
          topic.title,
          'topic',
          Array.from(topic.lessonMap.entries()).map(([lessonKey, lesson]) =>
            createGroupNode(lessonKey, lesson.title, 'lesson', lesson.children)
          )
        )
      )
    )
  );
};

const hasOnlyMaterialChildren = (node: TCourseTreeNode): boolean =>
  node.children.length > 0 &&
  node.children.every((child) => child.type === 'material');

const hasGroupedChildren = (node: TCourseTreeNode | undefined): boolean =>
  Boolean(node?.children.some((child) => child.type !== 'material'));

const getTreeNodeNumber = (
  node: TCourseTreeNode,
  type: 'module' | 'topic'
): string | null => {
  const match = `${node.key} ${node.title}`.match(
    type === 'module' ? /module[\s-]*(\d+)/i : /topic[\s-]*(\d+(?:\.\d+)*)/i
  );

  return match?.[1] ?? null;
};

const getTreeNodeSemanticKey = (node: TCourseTreeNode): string => {
  if (node.type === 'module') {
    return `module:${getTreeNodeNumber(node, 'module') ?? normalizeId(node.title)}`;
  }

  if (node.type === 'topic') {
    return `topic:${getTreeNodeNumber(node, 'topic') ?? normalizeId(node.title)}`;
  }

  if (node.type === 'lesson' || node.type === 'subtopic') {
    return `${node.type}:${normalizeId(node.title)}`;
  }

  return `${node.type}:${node.key}`;
};

const getAutomaticNodeMatch = (
  automaticNodes: TCourseTreeNode[],
  structuredNode: TCourseTreeNode
): TCourseTreeNode | undefined => {
  const exactMatch = automaticNodes.find(
    (node) => node.key === structuredNode.key
  );
  if (exactMatch) return exactMatch;

  const semanticKey = getTreeNodeSemanticKey(structuredNode);
  return automaticNodes.find(
    (node) => getTreeNodeSemanticKey(node) === semanticKey
  );
};

const mergeStructuredNodeWithAutomaticNode = (
  structuredNode: TCourseTreeNode,
  automaticNode: TCourseTreeNode | undefined
): TCourseTreeNode => {
  if (!automaticNode) return structuredNode;

  if (
    hasGroupedChildren(automaticNode) &&
    (structuredNode.children.length === 0 ||
      hasOnlyMaterialChildren(structuredNode))
  ) {
    return createGroupNode(
      structuredNode.key,
      structuredNode.title || automaticNode.title,
      structuredNode.type as Exclude<
        TCourseTreeNodeType,
        'material' | 'placeholder'
      >,
      automaticNode.children,
      {
        benefit: structuredNode.benefit,
        description: structuredNode.description,
      }
    );
  }

  const mergedChildren = structuredNode.children.map((child) =>
    mergeStructuredNodeWithAutomaticNode(
      child,
      getAutomaticNodeMatch(automaticNode.children, child)
    )
  );

  return createGroupNode(
    structuredNode.key,
    structuredNode.title,
    structuredNode.type as Exclude<
      TCourseTreeNodeType,
      'material' | 'placeholder'
    >,
    mergedChildren,
    {
      benefit: structuredNode.benefit,
      description: structuredNode.description,
    }
  );
};

const mergeStructuredTreeWithAutomaticTree = (
  structuredNodes: TCourseTreeNode[],
  automaticNodes: TCourseTreeNode[]
): TCourseTreeNode[] => {
  return structuredNodes.map((node) =>
    mergeStructuredNodeWithAutomaticNode(
      node,
      getAutomaticNodeMatch(automaticNodes, node)
    )
  );
};

export function buildCourseTree(
  items: TPlaylistContentItem[],
  courseStructure?: TCourseStructure | null,
  fallbackModuleCount = 6
): TCourseTreeNode[] {
  const automaticNodes = buildAutomaticCourseTree(items, fallbackModuleCount);

  if (courseStructure?.nodes?.length) {
    const currentIndex = getCurrentItemIndex(items);
    const structuredNodes = courseStructure.nodes.map((node) =>
      mapStructuredNode(node, items, currentIndex)
    );

    if (structuredNodes.length) {
      return mergeStructuredTreeWithAutomaticTree(
        structuredNodes,
        automaticNodes
      );
    }
  }

  return automaticNodes;
}

export function buildCourseStructureDraft(
  items: TPlaylistContentItem[],
  moduleCount: number
): TCourseStructure {
  const moduleMap = new Map<
    string,
    {
      id: string;
      title: string;
      topicMap: Map<
        string,
        {
          title: string;
          lessonMap: Map<string, { title: string; itemIndexes: number[] }>;
        }
      >;
    }
  >();

  Array.from({ length: Math.max(moduleCount, 1) }, (_, index) => {
    const moduleNumber = index + 1;
    moduleMap.set(`module-${moduleNumber}`, {
      id: `module-${moduleNumber}`,
      title: `Module ${moduleNumber}`,
      topicMap: new Map(),
    });
  });

  items.forEach((item, index) => {
    const moduleRef = resolveModule(item);
    const moduleEntry = moduleMap.get(moduleRef.key) ??
      moduleMap.get('module-1') ?? {
        id: moduleRef.key,
        title: moduleRef.title,
        topicMap: new Map<
          string,
          {
            title: string;
            lessonMap: Map<string, { title: string; itemIndexes: number[] }>;
          }
        >(),
      };
    const topic = resolveTopic(item);
    const topicKey = getTopicKey(topic);
    const topicEntry = moduleEntry.topicMap.get(topicKey) ?? {
      lessonMap: new Map<string, { title: string; itemIndexes: number[] }>(),
      title: getTopicTitle(topic),
    };
    const lesson = resolveLesson(item);
    const lessonKey = lesson?.key ?? `${topicKey}-lesson-materials`;
    const lessonEntry = topicEntry.lessonMap.get(lessonKey) ?? {
      itemIndexes: [],
      title: lesson?.title ?? 'Topic materials',
    };

    lessonEntry.itemIndexes.push(index);
    topicEntry.lessonMap.set(lessonKey, lessonEntry);
    moduleEntry.topicMap.set(topicKey, topicEntry);
    moduleMap.set(moduleEntry.id, moduleEntry);
  });

  return {
    nodes: Array.from(moduleMap.values()).map((moduleEntry) => ({
      children: Array.from(moduleEntry.topicMap.entries()).map(
        ([topicKey, topic]) => ({
          children: Array.from(topic.lessonMap.entries()).map(
            ([lessonKey, lesson]) => ({
              id: lessonKey,
              itemIndexes: lesson.itemIndexes,
              title: lesson.title,
              type: 'lesson' as const,
            })
          ),
          id: topicKey,
          title: topic.title,
          type: 'topic' as const,
        })
      ),
      id: moduleEntry.id,
      title: moduleEntry.title,
      type: 'module' as const,
    })),
    version: 1,
  };
}
